import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { AgentTaskTimingData } from '@sb/feathers-types';

export interface SessionTaskTimingOperations {
  /** Loads a session's timings. */
  list: (sessionID: string) => Promise<AgentTaskTimingData[]>;
  create: (
    sessionID: string,
    messageID: string,
    minutesByHand: number,
    minutesWithAgent: number,
    taskName: string | null,
  ) => Promise<AgentTaskTimingData>;
}

export interface UseSessionTaskTimingsOptions {
  sessionID: string | null;
  operations: SessionTaskTimingOperations;
  onError?: (error: Error) => void;
}

export interface UseSessionTaskTimingsResult {
  isLoaded: boolean;
  /** Persisted timings keyed by the assistant message they hang off. */
  timingByMessageID: ReadonlyMap<string, AgentTaskTimingData>;
  /** Persists one estimate for a message and records it once it lands. */
  log: (
    messageID: string,
    minutesByHand: number,
    minutesWithAgent: number,
    taskName: string | null,
  ) => Promise<void>;
}

const EMPTY: AgentTaskTimingData[] = [];

/**
 * The per-message clock's memory. Loads the session's rows once per
 * session and records each new one after its create resolves, so a clock
 * never shows an estimate Postgres does not have.
 */
export function useSessionTaskTimings(
  options: UseSessionTaskTimingsOptions,
): UseSessionTaskTimingsResult {
  const { sessionID, operations, onError } = options;

  const operationsRef = useRef(operations);
  const onErrorRef = useRef(onError);

  operationsRef.current = operations;
  onErrorRef.current = onError;

  const [timings, setTimings] = useState<AgentTaskTimingData[]>(EMPTY);

  const [loadedSessionID, setLoadedSessionID] = useState<string | null>(null);

  // Guards a stale load for a previous session against overwriting the
  // current one's rows.
  const loadTokenRef = useRef(0);

  useEffect(() => {
    loadTokenRef.current += 1;

    const myToken = loadTokenRef.current;

    setTimings(EMPTY);
    setLoadedSessionID(null);

    if (sessionID === null) return undefined;

    operationsRef.current
      .list(sessionID)
      .then((rows) => {
        if (myToken !== loadTokenRef.current) return;

        setLoadedSessionID(sessionID);

        // A row logged while the load was in flight is newer than
        // anything returned, so it wins on collision.
        setTimings((current) => {
          const known = new Set(current.map((row) => row.id));

          return [...current, ...rows.filter((row) => !known.has(row.id))];
        });
      })
      .catch((err: unknown) => {
        if (myToken !== loadTokenRef.current) return;

        onErrorRef.current?.(
          err instanceof Error ? err : new Error(String(err)),
        );
      });

    return undefined;
  }, [sessionID]);

  const timingByMessageID = useMemo(() => {
    const map = new Map<string, AgentTaskTimingData>();

    for (const row of timings) map.set(row.messageID, row);

    return map;
  }, [timings]);

  const log = useCallback(
    async (
      messageID: string,
      minutesByHand: number,
      minutesWithAgent: number,
      taskName: string | null,
    ) => {
      if (sessionID === null) return;

      const row = await operationsRef.current.create(
        sessionID,
        messageID,
        minutesByHand,
        minutesWithAgent,
        taskName,
      );

      setTimings((current) => [row, ...current]);
    },
    [sessionID],
  );

  return {
    timingByMessageID,
    log,
    isLoaded: sessionID !== null && loadedSessionID === sessionID,
  };
}
